/**
 * `/roles/:id` — the permission editor and the assignee list.
 *
 * The checkboxes come from `GET /api/permissions`, grouped by the part before
 * the dot, because that is how the backend groups them and hardcoding the list
 * here would drift the first time a permission is added.
 *
 * Saving sends the **complete set**, not a delta. That is the endpoint's
 * contract and it is the right one: two administrators editing the same role
 * cannot half-apply each other's changes. It does mean the editor holds a draft
 * and the Save button is real — so there is a "discard" next to it, and leaving
 * with unsaved changes is possible. That trade is deliberate; live-saving each
 * checkbox would write a dozen audit rows for one decision.
 *
 * A **built-in** role is read-only here. Its permissions come from the code
 * registry and the seed rewrites them wholesale, so the backend answers 403
 * `builtin_role`; showing live checkboxes would be offering an edit that cannot
 * be saved. Assigning and unassigning it still works — that part is data.
 *
 * Two permissions are called out on screen rather than left to look like the
 * rest: `notification.manage`, which can point the security channel at an
 * endpoint the holder controls, and `audit.read`, which is a window into
 * entities the reader has no other access to.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { SubjectDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { Columns } from '../../components/Columns';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { FormDialog } from '../../components/FormDialog';
import { EntityDetail } from '../../components/EntityDetail';
import { Panel } from '../../components/Panel';
import { SubjectAvatar } from '../../components/SubjectAvatar';
import { SubjectPicker } from '../../components/SubjectPicker';
import { subjectCaption, subjectKey } from '../../utils/subject';
import { useAsync } from '../../hooks/useAsync';
import { useSubjectSearch } from '../../hooks/useSubjectSearch';
import { useSubmit } from '../../hooks/useSubmit';
import { useAuth } from '../../auth/useAuth';
import { useSelfLockout } from '../../auth/useSelfLockout';
import { useRoleActions } from '../entities/roleActions';
import {
  assignRole,
  getRole,
  listPermissions,
  listRoleAssignees,
  searchSubjects,
  setRolePermissions,
  unassignRole,
} from '../../api/identity';
import { humanise, splitPermission } from '../../utils/format';

/**
 * Permissions whose reach is wider than their name suggests, as **keys**.
 *
 * The permission name is an identifier from the backend and is never
 * translated; only the warning beside it is.
 */
const DANGEROUS_KEYS = {
  'notification.manage': 'roleDetail.dangerous.notificationManage',
  'audit.read': 'roleDetail.dangerous.auditRead',
  'user.set_password': 'roleDetail.dangerous.userSetPassword',
  'user.impersonate': 'roleDetail.dangerous.userImpersonate',
  'resource.break_inheritance': 'roleDetail.dangerous.resourceBreakInheritance',
  'resource.transfer_ownership': 'roleDetail.dangerous.resourceTransferOwnership',
} as const;

type DangerKey = (typeof DANGEROUS_KEYS)[keyof typeof DANGEROUS_KEYS];

/** The warning for a permission, or nothing. Typed so `t()` stays checked. */
function dangerKeyFor(permission: string): DangerKey | undefined {
  return (DANGEROUS_KEYS as Record<string, DangerKey | undefined>)[permission];
}

export default function RoleDetailPage() {
  const { t } = useTranslation(['people', 'common']);
  const { id } = useParams();
  const roleId = Number(id);
  const { can } = useAuth();
  const navigate = useNavigate();

  const role = useAsync(() => getRole(roleId), [roleId]);
  const catalogue = useAsync(() => listPermissions(), []);
  const assignees = useAsync(() => listRoleAssignees(roleId), [roleId]);

  // The draft. Seeded from the role once it arrives, and from then on owned by
  // this screen until Save or Discard.
  const [draft, setDraft] = useState<Set<string> | null>(null);

  useEffect(() => {
    if (role.data) setDraft(new Set(role.data.role.permissions));
  }, [role.data]);

  // A built-in role's permission set comes from the code registry and is
  // rewritten by the seed, so the backend refuses an edit with 403
  // `builtin_role`. Leaving the checkboxes live would offer a change that
  // cannot be saved. Assignment is unaffected — that is ordinary data.
  const isBuiltin = role.data?.role.isBuiltin ?? false;
  const mayEdit = can('role.update') && !isBuiltin;
  const mayAssign = can('role.assign');

  const dirty = useMemo(() => {
    if (!draft || !role.data) return false;
    const current = role.data.role.permissions;
    return draft.size !== current.length || current.some((permission) => !draft.has(permission));
  }, [draft, role.data]);

  // The same set `RolesPage`'s rows offer, declared once in
  // `../entities/roleActions`. `EntityDetail` renders the buttons and mounts the
  // dialogs; nothing about either is written on this screen.
  const actions = useRoleActions({
    onChanged: (saved) => role.setData({ role: saved }),
    // The screen is about a role that no longer exists.
    onDeleted: () => navigate('/roles'),
  });

  // Unticking a permission you hold through this role, or unassigning it from
  // yourself or a group you are in, can take it away from you. The server
  // says so, and `lockout` asks before it repeats the call.
  const lockout = useSelfLockout();

  const save = useSubmit(async () => {
    if (!draft) return undefined;
    const permissions = [...draft];
    const saved = await lockout.guard((accept) =>
      setRolePermissions(roleId, { permissions }, accept),
    );
    role.setData(saved);
    return saved;
  });

  // --- assignees ------------------------------------------------------------

  const [assigning, setAssigning] = useState(false);
  const [subject, setSubject] = useState<SubjectDto | null>(null);
  const candidates = useSubjectSearch(searchSubjects, { enabled: assigning });

  // Closing forgets the pick and the term, so the next open starts empty.
  function closeAssigning(): void {
    setAssigning(false);
    setSubject(null);
    candidates.reset();
  }

  const assign = useSubmit(async () => {
    if (!subject) return undefined;
    const result = await assignRole(roleId, { subjectType: subject.type, subjectId: subject.id });
    setSubject(null);
    candidates.reset();
    assignees.reload();
    return result;
  });

  const unassign = useSubmit(async (entry: SubjectDto) => {
    const result = await lockout.guard((accept) =>
      unassignRole(roleId, entry.type, entry.id, accept),
    );
    assignees.reload();
    return result;
  });

  function toggle(permission: string) {
    setDraft((current) => {
      const next = new Set(current ?? []);
      if (next.has(permission)) next.delete(permission);
      else next.add(permission);
      return next;
    });
  }

  function toggleDomain(permissions: string[], all: boolean) {
    setDraft((current) => {
      const next = new Set(current ?? []);
      for (const permission of permissions) {
        if (all) next.delete(permission);
        else next.add(permission);
      }
      return next;
    });
  }

  return (
    <AsyncContent state={role}>
      {({ role: record }) => (
        <EntityDetail
          backTo="/roles"
          backLabel={t('roleDetail.back')}
          title={record.name}
          badges={
            record.isBuiltin && (
              <Chip size="small" variant="outlined" label={t('roleDetail.builtin')} />
            )
          }
          description={record.description ?? t('roleDetail.noDescription')}
          entity={actions.forRow(record)}
          actions={
            mayEdit && (
              <>
                <Button
                  disabled={!dirty || save.pending}
                  onClick={() => setDraft(new Set(record.permissions))}
                >
                  {t('roleDetail.discard')}
                </Button>
                <Button
                  variant="contained"
                  disabled={!dirty || save.pending}
                  onClick={() => void save.submit()}
                >
                  {save.pending ? t('roleDetail.saving') : t('roleDetail.save')}
                </Button>
              </>
            )
          }
          error={save.error}
          notices={
            <>
              {record.isBuiltin && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('roleDetail.builtinNotice')}
                </Alert>
              )}
              {dirty && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('roleDetail.unsaved')}
                </Alert>
              )}
            </>
          }
        >
          <Columns split={[2, 1]} from="lg">
            <Panel title={t('roleDetail.permissions')}>

              <AsyncContent state={catalogue} placeholder="spinner">
                {({ byDomain }) => (
                  <Stack spacing={3}>
                    {Object.entries(byDomain).map(([domain, permissions]) => {
                      const allOn = permissions.every((permission) => draft?.has(permission));

                      return (
                        <Box key={domain}>
                          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                            >
                              {domain}
                            </Typography>
                            {mayEdit && (
                              <Button
                                size="small"
                                onClick={() => toggleDomain(permissions, allOn)}
                              >
                                {allOn ? t('roleDetail.clearAll') : t('roleDetail.selectAll')}
                              </Button>
                            )}
                          </Stack>

                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                            }}
                          >
                            {permissions.map((permission) => {
                              const { verb } = splitPermission(permission);
                              const warningKey = dangerKeyFor(permission);

                              return (
                                <FormControlLabel
                                  key={permission}
                                  disabled={!mayEdit}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={draft?.has(permission) ?? false}
                                      onChange={() => toggle(permission)}
                                    />
                                  }
                                  label={
                                    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                                      <Typography variant="body2">{humanise(verb)}</Typography>
                                      {warningKey && (
                                        <Tooltip title={t(warningKey)}>
                                          <WarningAmberIcon
                                            fontSize="inherit"
                                            color="warning"
                                            sx={{ fontSize: 16 }}
                                          />
                                        </Tooltip>
                                      )}
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontFamily: 'monospace' }}
                                      >
                                        {permission}
                                      </Typography>
                                    </Stack>
                                  }
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </AsyncContent>
            </Panel>

            <Panel
              title={t('roleDetail.heldBy')}
              actions={
                mayAssign && (
                  <Button
                    size="small"
                    startIcon={<PersonAddAltIcon />}
                    onClick={() => setAssigning(true)}
                  >
                    {t('roleDetail.assign')}
                  </Button>
                )
              }
              sx={{ alignSelf: 'flex-start' }}
            >

              {unassign.error && <ErrorState error={unassign.error} severity="warning" />}

              <AsyncContent state={assignees} placeholder="spinner">
                {({ assignees: rows }) =>
                  rows.length === 0 ? (
                    <EmptyState
                      dense
                      title={t('roleDetail.noAssigneesTitle')}
                      description={t('roleDetail.noAssigneesDescription')}
                    />
                  ) : (
                    <List dense disablePadding>
                      {rows.map((entry) => (
                        <ListItem
                          key={subjectKey(entry)}
                          disableGutters
                          secondaryAction={
                            mayAssign && (
                              <Tooltip title={t('roleDetail.unassign')}>
                                <span>
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    disabled={unassign.pending}
                                    onClick={() => void unassign.submit(entry)}
                                  >
                                    <DeleteOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )
                          }
                        >
                          <ListItemAvatar>
                            <SubjectAvatar type={entry.type} />
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Link
                                component={RouterLink}
                                to={entry.type === 'group' ? `/groups/${entry.id}` : `/users/${entry.id}`}
                                underline="hover"
                                color="inherit"
                              >
                                {entry.displayName}
                              </Link>
                            }
                            secondary={subjectCaption(entry)}
                            slotProps={{ secondary: { variant: 'caption' } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )
                }
              </AsyncContent>
            </Panel>
          </Columns>

          <FormDialog
            open={assigning}
            title={t('roleDetail.assignDialog.title', { name: record.name })}
            submitLabel={t('roleDetail.assignDialog.submit')}
            pending={assign.pending}
            error={assign.error}
            disabled={subject === null}
            onClose={closeAssigning}
            onSubmit={async () => {
              if (await assign.submit()) closeAssigning();
            }}
          >
            <SubjectPicker
              options={candidates.options}
              value={subject}
              loading={candidates.loading}
              label={t('roleDetail.assignDialog.subject')}
              helperText={t('roleDetail.assignDialog.help')}
              onChange={setSubject}
              onQueryChange={candidates.setQuery}
              autoFocus
            />
          </FormDialog>
          {lockout.dialog}
        </EntityDetail>
      )}
    </AsyncContent>
  );
}
